import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import ProgressBar from "../../ProgressBars/ProgressBar";
import ReversedProgressBar from "../../ProgressBars/ReversedProgressBar";
import { alpha } from "@mui/material/styles";
import { theme } from "../../../Theme/index";
import { getUrl } from "src/Utils/Common";
import { useMediaQuery } from "@mui/material";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import { useSelector } from "react-redux";

function SourceShareTable({
  selectedSku: propSelectedSku,
  setSelectedSku: propSetSelectedSku,
}) {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const projectId = useSelector((state) => state.userProject.projectId);
  const projectName = projectId != null ? String(projectId) : null;
  const simRequestHeaders = {
    "Content-Type": "application/json",
    "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
    "x-access-token": localStorage.getItem("userId") || "",
  };
  const [tableData, setTableData] = useState([]); // Holds sanitized API response data
  const [skus, setSkus] = useState([]);
  const [selectedSku, setSelectedSku] = useState(propSelectedSku || "SKU1");
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state

  const currentSelectedSku =
    propSelectedSku !== undefined ? propSelectedSku : selectedSku;
  const setCurrentSelectedSku =
    propSetSelectedSku !== undefined ? propSetSelectedSku : setSelectedSku;

  // Sanitize invalid JSON values like NaN
  const sanitizeJSON = (text) => {
    return text.replace(/NaN/g, "null");
  };

  const fetchData = async (sku) => {
    if (!sku || !projectName) {
      setTableData([]); // Clear table if SKU is deselected
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${getUrl()}/sim/brand_interaction`, {
        method: "POST",
        headers: simRequestHeaders,
        body: JSON.stringify({
          project_name: projectName,
          sku: sku,
          n: 5,
          low: -0.1,
          high: 0.1,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const text = await response.text();
      const sanitizedText = sanitizeJSON(text); // Replace NaN with null
      const result = JSON.parse(sanitizedText); // Parse sanitized JSON
      const rows = parseTableData(result); // Parse sanitized data into table rows
      setTableData(rows);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Function to fetch SKUs
  useEffect(() => {
    async function fetchSkus() {
      if (!projectName) {
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(
          `${getUrl()}/sim/get_sku_by_project_name?project_name=${encodeURIComponent(projectName)}`,
          {
            method: "GET",
            headers: simRequestHeaders,
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const skus = await response.json();
        setSkus(skus.skus);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }

    fetchSkus();
    fetchData(currentSelectedSku);
  }, [currentSelectedSku, projectName]);

  const handleChange = (event, newValue) => {
    setCurrentSelectedSku(newValue || "");
    if (!newValue) {
      setTableData([]); // Clear table if SKU is deselected
      return;
    }
    fetchData(newValue); // ✅ Use the existing function instead of redefining it
  };

  const parseTableData = (data) => {
    const parsedRows = [];
    const keys = Object.keys(data);
    keys.forEach((key) => {
      const brands = data[key];
      Object.keys(brands).forEach((brand) => {
        const details = brands[brand];
        parsedRows.push({
          brandValueIdx: details?.abs_interaction || 0,
          brandName: brand,
          pricingMinus: details?.Price_level || details?.price || null,
          pricingPlus: details?.zero_Price || null,
          rgmIdx: details?.fair_share || 0,
        });
      });
    });
    return parsedRows;
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      padding: "5px",
      color: theme.palette.primary.dark,
      fontSize: theme.typography.fontSize.xxsmall,
      textAlign: "center",
      justifyContent: "center",
      "@media (max-width: 600px)": {
        fontSize: "8px", // Reduce font size on mobile
      },
    },
    [`&.${tableCellClasses.body}`]: {
      textTransform: "capitalize",
      color: theme.palette.primary.dark,
      fontSize: 10,
      textAlign: "center",
      padding: "3px",
      "@media (max-width: 600px)": {
        fontSize: "8px", // Reduce font size on mobile
        padding: "2px", // Reduce padding to save space
      },
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    height: 30, // Reduce the row height
  }));

  if (loading) {
    return (
      <Box
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: {
              xs: "0.9rem",
              sm: "1.2rem",
              md: "1.3rem",
            },
            px: {
              xs: 0.5,
              sm: 0.75,
              md: 1,
            },
            color: theme.palette.primary.main,
            textAlign: "center",
            m: 0,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 48,
          }}
        >
          Brand Interaction
        </Typography>
        <FacebookCircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: {
              xs: "0.9rem",
              sm: "1.2rem",
              md: "1.3rem",
            },
            px: {
              xs: 0.5,
              sm: 0.75,
              md: 1,
            },
            color: theme.palette.primary.main,
            textAlign: "center",
            m: 0,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 48,
          }}
        >
          Brand Interaction
        </Typography>
        <FacebookCircularProgress
          loading={false}
          hasData={false}
          onRefresh={() => {
            setError(null);
            fetchData(currentSelectedSku);
          }}
          message={`${error} - Click to retry`}
        />
      </Box>
    );
  }

  return (
    <>
      <Autocomplete
        options={skus}
        value={currentSelectedSku}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select SKU"
            variant="outlined"
            size="small"
            placeholder="Search or select SKU"
            sx={{
              width: "100%",
              maxWidth: 300, // Ensure dropdown doesn't shrink too much
              "@media (max-width: 600px)": {
                width: "100%", // Full width on mobile
                fontSize: "10px",
              },
            }}
          />
        )}
        sx={{
          width: 300,
          marginBottom: 2,
          ml: 3,
          "@media (max-width: 600px)": {
            width: "100%", // Make sure it stretches properly on mobile
            ml: 0, // Remove left margin on small screens
          },
        }}
      />

      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: {
            xs: "0.9rem",
            sm: "1.2rem",
            md: "1.3rem",
          },
          px: {
            xs: 0.5,
            sm: 0.75,
            md: 1,
          },
          color: theme.palette.primary.main,
          textAlign: "center",
          m: 0,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 48,
        }}
      >
        Brand Interaction
      </Typography>

      {currentSelectedSku ? (
        tableData && tableData.length > 0 ? (
          <TableContainer
            sx={{
              height: 300,
              overflowX: "auto", // Enables horizontal scrolling on mobile
              "@media (max-width: 600px)": {
                width: "100%", // Ensure it fits within the screen width
              },
            }}
          >
            {" "}
            <Table aria-label="customized table">
              <TableHead
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#ffffff",
                  zIndex: 10,
                  // "@media (max-width: 600px)": {
                  //   display: "none", // Hide table headers on mobile for a cleaner look
                  // },
                }}
              >
                <TableRow>
                  <StyledTableCell>Sourcing vs Fair Share</StyledTableCell>
                  <StyledTableCell>Pricing (-10%)</StyledTableCell>
                  <StyledTableCell>Brand</StyledTableCell>
                  <StyledTableCell>Pricing (+10%)</StyledTableCell>
                  <StyledTableCell>Sourcing vs Fair Share</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row, idx) => (
                  <StyledTableRow key={idx}>
                    <StyledTableCell>
                      {row.brandValueIdx.toFixed(2)}
                    </StyledTableCell>

                    {/* Hide Pricing (-10%) column on small screens */}
                    {/* {!isMobile && ( */}
                    <StyledTableCell>
                      {row.pricingMinus !== null ? (
                        <ReversedProgressBar
                          bgcolor={"#CD393B"}
                          progressChild={row.pricingMinus * 10}
                          number={row.pricingMinus.toFixed(2)}
                        />
                      ) : (
                        "N/A"
                      )}
                    </StyledTableCell>
                    {/* )} */}

                    <StyledTableCell>{row.brandName}</StyledTableCell>

                    {/* Hide Pricing (+10%) column on small screens */}
                    {/* {!isMobile && ( */}
                    <StyledTableCell>
                      {row.pricingPlus !== null ? (
                        <ProgressBar
                          bgcolor={"#08306B"}
                          progressChild={row.pricingPlus * 10}
                          number={row.pricingPlus.toFixed(2)}
                        />
                      ) : (
                        "N/A"
                      )}
                    </StyledTableCell>
                    {/* )} */}

                    <StyledTableCell>{row.rgmIdx.toFixed(2)}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
            }}
          >
            <FacebookCircularProgress
              loading={false}
              hasData={false}
              onRefresh={() => {
                setError(null);
                fetchData(currentSelectedSku);
              }}
              message="No data available - Click to retry"
            />
          </Box>
        )
      ) : (
        <Typography color="textSecondary" sx={{ ml: 3 }}>
          Please select an SKU to see data.
        </Typography>
      )}
    </>
  );
}

export default SourceShareTable;
